import React, { useEffect, useState } from "react";

import {
  Box,
  Caption,
  Divider,
  Flex,
  SectionSubheading,
  Select,
} from "@olist/design-system";

import { InputHandling } from "./components";
import {
  IField,
  formFields,
  getFieldsByReason,
  getReasonOptions,
  getSubReasonOptions,
  subjectOptions,
} from "./constants";

interface ISystemFormStepProps {
  formData: Record<string, string>;
  handleFormDataChange: (data: Record<string, any>) => void;
  fieldError: string[];
  onCleanError: (param: string) => void;
}

export function SystemFormStep({
  fieldError,
  formData,
  handleFormDataChange,
  onCleanError,
}: ISystemFormStepProps) {
  const [subject, setSubject] = useState<string | undefined>("");
  const [reason, setReason] = useState<string | undefined>("");
  const [subReason, setSubReason] = useState<string | undefined>("");
  const [reasonList, setReasonList] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [subReasonList, setSubReasonList] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [fields, setFields] = useState<IField[]>([]);

  const hasSubreason =
    getSubReasonOptions(formData[formFields.reason.id])[0] !== undefined &&
    getSubReasonOptions(formData[formFields.reason.id])[0]?.value !== "";

  const shouldRenderReason = Boolean(formData[formFields.subject.id]);

  const shouldRenderForm = !hasSubreason
    ? shouldRenderReason && shouldRenderReason
    : shouldRenderReason &&
      shouldRenderReason &&
      Boolean(formData[formFields.subreason.id]);

  const handleSubjectChange = (value) => {
    let resetFields;
    if (fields.length > 0) {
      resetFields = fields
        ?.filter(({ id }) => id !== "Description")
        .reduce((acc, cur) => ({ ...acc, [cur.id as string]: "" }), {});
    }
    handleFormDataChange({
      Description: formData.Description,
      [formFields.subject.id]: value?.[0]?.value || "--None--",
      [formFields.reason.id]: "",
      [formFields.subreason.id]: "",
      ...resetFields,
    });
    setFields([]);
    onCleanError("Assunto");
  };

  const handleReasonChange = (value) => {
    let resetFields;
    if (fields.length > 0) {
      resetFields = fields?.reduce(
        (acc, cur) => ({ ...acc, [cur.id as string]: "" }),
        {}
      );
    }
    handleFormDataChange({
      Description: formData.Description,
      [formFields.reason.id]: value?.[0]?.value || "--None--",
      [formFields.subreason.id]: "",
      ...resetFields,
    });
    setFields([]);
    onCleanError("Motivo");
  };

  const handleSubReasonChange = (value) => {
    console.log({ value });
    let resetFields;
    if (fields.length > 0) {
      resetFields = fields?.reduce(
        (acc, cur) => ({ ...acc, [cur.id as string]: "" }),
        {}
      );
    }
    handleFormDataChange({
      Description: formData.Description,
      [formFields.subreason.id]: value?.[0]?.value || "--None--",
      ...resetFields,
    });
    onCleanError("Submotivo");
  };

  const shouldRenderReasonForm =
    formData[formFields.subject.id] === undefined ||
    formData[formFields.subject.id] !== subject ||
    reasonList.length === 0 ||
    reason === undefined;
  const shouldRenderSubReasonForm =
    formData[formFields.subject.id] === undefined ||
    formData[formFields.subject.id] !== subject ||
    formData[formFields.reason.id] === undefined ||
    formData[formFields.reason.id] !== reason ||
    subReasonList.length === 0 ||
    subReason === undefined;

  useEffect(() => {
    if (formData[formFields.subject.id] !== subject) {
      setSubject(formData[formFields.subject.id]);
      const reasons = getReasonOptions(formData[formFields.subject.id]);
      setReasonList(reasons);
      setSubReasonList([]);
      setReason("");
      setSubReason(undefined);
    }

    if (formData[formFields.reason.id] !== reason) {
      setReason(formData[formFields.reason.id]);
      const subReasons = getSubReasonOptions(formData[formFields.reason.id]);
      setSubReasonList(subReasons);
      setSubReason("");
    }

    if (formData[formFields.subreason.id] !== subReason) {
      setSubReason(formData[formFields.subreason.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (formData[formFields.subject.id] && formData[formFields.reason.id]) {
      const newFields = getFieldsByReason({
        subject: formData[formFields.subject.id],
        reason: formData[formFields.reason.id],
        subreason: formData[formFields.subreason.id],
      });
      setFields(newFields);
    }
  }, [shouldRenderForm, formData]);

  return (
    <>
      <Box>
        <Divider />
        <SectionSubheading
          marginBottom="24px"
          marginTop="24px"
          style={{ fontWeight: 500 }}
          bold
        >
          Dúvidas sobre o sistema
        </SectionSubheading>
      </Box>
      <Flex
        as="form"
        flexDirection="column"
        action="https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00DHr000002jN5F"
        method="POST"
      >
        <Flex gap="stack.small" flexDirection="column">
          <Select
            {...formFields.subject}
            onSelect={handleSubjectChange}
            multi={false}
            feedback={fieldError.includes("Assunto") ? "error" : undefined}
          >
            {subjectOptions.map((option) => (
              <Select.Option
                key={option.value}
                value={option.value}
                selected={subject === option.value}
              >
                {option.label}
              </Select.Option>
            ))}
          </Select>

          {!shouldRenderReasonForm && (
            <Select
              {...formFields.reason}
              onSelect={handleReasonChange}
              multi={false}
              value={reason}
              feedback={fieldError.includes("Motivo") ? "error" : undefined}
            >
              {reasonList.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  selected={reason === option.value}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          )}
          {!shouldRenderSubReasonForm && (
            <Select
              {...formFields.subreason}
              onSelect={handleSubReasonChange}
              multi={false}
              feedback={fieldError.includes("Submotivo") ? "error" : undefined}
            >
              {subReasonList.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  selected={subReason === option.value}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          )}

          {fields.length > 0 && (
            <>
              <Divider />
              <Flex
                border={
                  fieldError.includes("Campos complementares")
                    ? "2px solid red"
                    : undefined
                }
                padding={
                  fieldError.includes("Campos complementares")
                    ? "inset.small"
                    : undefined
                }
                flexDirection="column"
                borderRadius="12px"
                marginTop="stack.1xsmall"
              >
                <Caption bold>Campos complementares</Caption>
                {fields.map((field) => (
                  <InputHandling
                    key={field.id}
                    formData={formData}
                    handleFormDataChange={handleFormDataChange}
                    {...field}
                  />
                ))}
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </>
  );
}
